/**
 * Helpers for the dev-only royalty → VIP Points mock layer driven by `DevDebugHeader`.
 *
 * Shaped after `dev-impersonation.utils`, deliberately: one module owns the storage key, the
 * read, the write and the guard, and the panel imports it the same way it imports impersonation.
 *
 * WHY THE MOCK EXISTS. The perk is fed by two external providers nobody on this side can put
 * numbers into — the royalty data handler behind `GET /vip/royalty-points/preview/`, and the
 * passive-income proxy that supplies the claimable balance. Locally both answer with nothing, so
 * all three surfaces render empty, which is ALSO exactly what they correctly render for a holder
 * the perk does not pay. Without a mock, "broken" and "working, not eligible" are the same screen.
 *
 * WHY IT IS A SELECTION AND NOT A FLAG. Seeing one state is not the point — the states are the
 * point, and two of the six are HTTP failures rather than payloads. So a single stored value names
 * the state, and "not stored" is off. Two switches for one thing would only create a state where
 * they disagree.
 */

/** Every state the mock layer can be put into. */
export type RoyaltyMockState =
  /** VIP with a rate, real royalty accrued, real points. The panel and both estimates render. */
  | 'eligible'
  /** Eligible, nothing accrued — the HONEST zero, stated in words rather than as a "0". */
  | 'empty'
  /** Not a VIP at all. `vip_level: null`, which is NOT level 0. Every surface renders nothing. */
  | 'non_vip'
  /** VIP 0 — a real level that converts at a rate of 0. Also nothing, for a different reason. */
  | 'vip_zero'
  /** The provider is down: the endpoint's 503. No payload, so the hook's `unavailable` fires. */
  | 'unavailable'
  /** The perk is switched off: the endpoint's 404. The hook's `featureOff` fires. */
  | 'feature_off';

/** `sessionStorage`, not `localStorage`, for the reason impersonation gives: a session of made-up
 * royalty figures must not silently survive a browser restart and be mistaken for real data in a
 * tab opened days later. */
export const DEV_ROYALTY_MOCKS_STORAGE_KEY = 'dev:royaltyMockState';

/** The stored value when the mock is off. Absent is off too; this is what the select submits. */
export const ROYALTY_MOCK_OFF = 'off';

/** What the panel's select holds: a state, or off. */
export type RoyaltyMockSelection = RoyaltyMockState | typeof ROYALTY_MOCK_OFF;

/** The states in the order they are worth stepping through, and how the panel labels them. */
export const ROYALTY_MOCK_STATE_OPTIONS: { value: RoyaltyMockState; label: string }[] = [
  { value: 'eligible', label: 'eligible — VIP with a rate, royalty accrued' },
  { value: 'empty', label: 'empty — eligible, nothing accrued yet' },
  { value: 'non_vip', label: 'non_vip — not a VIP (level null)' },
  { value: 'vip_zero', label: 'vip_zero — VIP 0, converts at x0.00' },
  { value: 'unavailable', label: 'unavailable — provider down (503)' },
  { value: 'feature_off', label: 'feature_off — perk switched off (404)' },
];

export const ROYALTY_MOCK_STATES: readonly RoyaltyMockState[] = ROYALTY_MOCK_STATE_OPTIONS.map(
  (option) => option.value
);

/**
 * What an unreadable stored value falls back to once the mock is on. The eligible case, because it
 * is the only one where all three surfaces have something to show — a junk value that silently
 * selected `non_vip` would render the blank screen the mock exists to rule out.
 */
export const DEFAULT_ROYALTY_MOCK_STATE: RoyaltyMockState = 'eligible';

/** Warned once per distinct bad value: this runs per request, and repeating it is only noise. */
const warnedValues = new Set<string>();

const isRoyaltyMockState = (value: string): value is RoyaltyMockState =>
  (ROYALTY_MOCK_STATES as readonly string[]).includes(value);

const readRawSelection = (): string => {
  if (typeof window === 'undefined') return '';
  try {
    return window.sessionStorage.getItem(DEV_ROYALTY_MOCKS_STORAGE_KEY) || '';
  } catch {
    return '';
  }
};

/** The panel's current selection, for rendering the control. Never throws, never guesses "on". */
export const getRoyaltyMockSelection = (): RoyaltyMockSelection => {
  if (!isRoyaltyMocksEnabled()) return ROYALTY_MOCK_OFF;
  return getRoyaltyMockState();
};

/** Commit a selection. `off` removes the key rather than storing a sentinel nothing else reads. */
export const setRoyaltyMockSelection = (selection: RoyaltyMockSelection): void => {
  if (typeof window === 'undefined') return;
  try {
    if (selection === ROYALTY_MOCK_OFF) {
      window.sessionStorage.removeItem(DEV_ROYALTY_MOCKS_STORAGE_KEY);
    } else {
      window.sessionStorage.setItem(DEV_ROYALTY_MOCKS_STORAGE_KEY, selection);
    }
  } catch {
    /* storage disabled — the mock simply stays off, which is the safe direction */
  }
};

/**
 * THE single decision point for whether the royalty mocks run at all. Both call sites
 * (`vip-royalty-points.service`, `passive-income.service`) ask this and nothing re-checks it, so a
 * new surface cannot pick up the mock and miss the guard.
 *
 * THE PRODUCTION GUARD MATTERS MORE NOW, NOT LESS. While this was an env variable, turning the
 * mock on took a build. It is now a `sessionStorage` key, which anybody can set from a browser
 * console on any deployed environment — so the only thing standing between a real holder and
 * fabricated royalty figures is this line. And they ARE fabricated money: a page reading "1,284
 * RMV convertible, ≈3,210 VIP Points" is indistinguishable from the truth from where the holder is
 * standing. `REACT_APP_USE_MARKET_MOCKS` needs no such guard because a fake listing is obviously
 * fake and nobody's balance depends on it.
 *
 * `process.env.NODE_ENV` is written out in full and never destructured: CRA's DefinePlugin
 * substitutes it literally at build time, so a production bundle compiles this to
 * `if ('production' === 'production') return false` and drops the rest. A destructured read would
 * defeat that and ship the canned figures.
 *
 * Deliberately `!== 'production'` while `DevDebugHeader`'s own `IS_DEV` is `=== 'development'`.
 * They answer different questions and are allowed to differ: the panel is a UI gate (only the dev
 * server offers the control), this is a safety gate (nothing but production is refused), which
 * leaves a staging or QA build mockable by hand while production never is.
 */
export const isRoyaltyMocksEnabled = (): boolean => {
  if (process.env.NODE_ENV === 'production') return false;

  return readRawSelection() !== '';
};

/**
 * Which state to serve. Only meaningful once `isRoyaltyMocksEnabled()` is true.
 *
 * A value the select cannot produce can still arrive — someone edits `sessionStorage` by hand — so
 * it falls back and says so rather than throwing. A dev-only convenience must not be able to take
 * the page down.
 */
export const getRoyaltyMockState = (): RoyaltyMockState => {
  const raw = readRawSelection().trim().toLowerCase();
  if (!raw) return DEFAULT_ROYALTY_MOCK_STATE;
  if (isRoyaltyMockState(raw)) return raw;

  if (!warnedValues.has(raw)) {
    warnedValues.add(raw);
    // eslint-disable-next-line no-console
    console.warn(
      `[royalty mocks] Unknown ${DEV_ROYALTY_MOCKS_STORAGE_KEY} value "${raw}" — falling back to ` +
        `"${DEFAULT_ROYALTY_MOCK_STATE}". Known states: ${ROYALTY_MOCK_STATES.join(', ')}.`
    );
  }

  return DEFAULT_ROYALTY_MOCK_STATE;
};
